import type { Feature, FeatureRequest, Lead, Visit } from '../../../shared/types';

/** Pure watchlist grouping (M4): features → the leads who asked, with visit context. */

export interface WatchlistAsk {
  request: FeatureRequest;
  lead: Lead;
  /** The visit the ask was captured on, when any. */
  visit: Visit | null;
}

export interface WatchlistEntry {
  feature: Feature;
  asks: WatchlistAsk[];
  /** Distinct leads asking. */
  leadCount: number;
  /** Distinct leads a launch drafts a good-news email for: owners already won get none (D34). */
  draftCount: number;
}

/** Missing features first (most-requested on top), launched after (newest launch first). */
export const buildWatchlist = (
  features: readonly Feature[],
  requests: readonly FeatureRequest[],
  leads: readonly Lead[],
  visits: readonly Visit[],
): WatchlistEntry[] => {
  const leadById = new Map(leads.map((l) => [l.id, l]));
  const visitById = new Map(visits.map((v) => [v.id, v]));
  const byFeature = new Map<string, WatchlistAsk[]>();
  for (const request of requests) {
    const lead = leadById.get(request.lead_id);
    if (!lead) continue; // orphaned (lead deleted locally) — pruned on next sync
    const ask: WatchlistAsk = {
      request,
      lead,
      visit: request.visit_id ? (visitById.get(request.visit_id) ?? null) : null,
    };
    byFeature.set(request.feature_id, [...(byFeature.get(request.feature_id) ?? []), ask]);
  }
  const entries = features.map((feature) => {
    const asks = [...(byFeature.get(feature.id) ?? [])].sort((a, b) => {
      const at = a.visit?.visited_at ?? '';
      const bt = b.visit?.visited_at ?? '';
      return at < bt ? 1 : at > bt ? -1 : 0;
    });
    return {
      feature,
      asks,
      leadCount: new Set(asks.map((a) => a.lead.id)).size,
      draftCount: new Set(asks.filter((a) => a.lead.stage !== 'closed_won').map((a) => a.lead.id)).size,
    };
  });
  return entries.sort((a, b) => {
    if (a.feature.status !== b.feature.status) return a.feature.status === 'missing' ? -1 : 1;
    if (a.feature.status === 'missing') {
      if (b.leadCount !== a.leadCount) return b.leadCount - a.leadCount;
      return a.feature.name.localeCompare(b.feature.name, undefined, { sensitivity: 'base' });
    }
    const al = a.feature.launched_at ?? '';
    const bl = b.feature.launched_at ?? '';
    return al < bl ? 1 : al > bl ? -1 : 0;
  });
};
